import React, { useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useTrainingReportCostsData from '../hooks/useTrainingReportCostsData';

// Backend `currency` alanını doğrulamaz (serbest metin, büyük harfe çevrilerek saklanır —
// doc §4.1), ama diğer eğitim sayfalarındaki gibi burada sabit bir dropdown kullanılıyor
// (bkz. training-planning/components/PlanningForm.jsx — aynı desen).
const CURRENCY_OPTIONS = [
    { value: 'TRY', label: 'TRY' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' }
];

/**
 * Rapor detayındaki maliyet satırları. `PlanningInstructorsTab.jsx` ile aynı desen
 * (add + delete; satır düzenleme yok). Bkz. docs/LMS-Egitim-Raporlari-API.md §4.
 */
const TrainingReportCostsTab = ({ trainingReportId, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const { costsApi, isLoading, isAdding, isDeleting, addCost, removeCost } = useTrainingReportCostsData(trainingReportId);

    const [estimatedCost, setEstimatedCost] = useState(null);
    const [costSpent, setCostSpent] = useState(null);
    const [currency, setCurrency] = useState(null);

    const costs = useMemo(() => costsApi?.items || [], [costsApi]);

    const resetForm = () => {
        setEstimatedCost(null);
        setCostSpent(null);
        setCurrency(null);
    };

    const handleAdd = async () => {
        const hasEstimated = estimatedCost !== null && estimatedCost !== undefined;
        const hasSpent = costSpent !== null && costSpent !== undefined;
        if (!currency || (!hasEstimated && !hasSpent)) {
            toast.current?.show({ severity: 'error', summary: th('trainingCatalog.missingField'), detail: th('trainingReport.costs.validationMessage'), life: 4000 });
            return;
        }
        const success = await addCost({
            estimatedCost: hasEstimated ? Number(estimatedCost) : null,
            costSpent: hasSpent ? Number(costSpent) : null,
            currency
        });
        if (success) resetForm();
    };

    const handleRemove = (row) => {
        confirmDialog({
            message: th('trainingReport.costs.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => removeCost(row.id)
        });
    };

    const actionBodyTemplate = (row) => <i className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('common.delete')} onClick={() => handleRemove(row)} />;
    const costBodyTemplate = (row, field) => (row[field] != null ? `${row[field]} ${row.currency || ''}`.trim() : '');

    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />
            {!readOnly && (
                <div className="flex flex-wrap align-items-end gap-2 mb-3">
                    <div style={{ minWidth: '12rem' }}>
                        <label className="block mb-1">{th('trainingReport.costs.fields.estimatedCost')}</label>
                        <InputNumber value={estimatedCost} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setEstimatedCost(e.value)} className="w-full" />
                    </div>
                    <div style={{ minWidth: '12rem' }}>
                        <label className="block mb-1">{th('trainingReport.costs.fields.costSpent')}</label>
                        <InputNumber value={costSpent} min={0} mode="decimal" maxFractionDigits={4} onValueChange={(e) => setCostSpent(e.value)} className="w-full" />
                    </div>
                    <div style={{ minWidth: '10rem' }}>
                        <label className="block mb-1">{th('trainingReport.costs.fields.currency')}</label>
                        <Dropdown
                            value={currency}
                            options={CURRENCY_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setCurrency(e.value)}
                            placeholder={th('trainingReport.costs.fields.selectPlaceholder')}
                            className="w-full"
                            appendTo={document.body}
                        />
                    </div>
                    <Button label={th('trainingReport.costs.addButton')} icon="pi pi-plus" onClick={handleAdd} loading={isAdding} />
                </div>
            )}

            <DataTable value={costs} loading={isLoading || isDeleting} dataKey="id" emptyMessage={th('trainingReport.costs.emptyMessage')} className="p-datatable-gridlines" showGridlines>
                {!readOnly && <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" />}
                <Column field="estimatedCost" header={th('trainingReport.costs.columnEstimatedCost')} body={(row) => costBodyTemplate(row, 'estimatedCost')} />
                <Column field="costSpent" header={th('trainingReport.costs.columnCostSpent')} body={(row) => costBodyTemplate(row, 'costSpent')} />
                <Column field="currency" header={th('trainingReport.costs.columnCurrency')} style={{ width: '8rem' }} />
            </DataTable>
        </div>
    );
};

export default TrainingReportCostsTab;
